import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { MagnifyingGlass, FileCsv, FilePdf, Plus, Package, Barcode } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const FLOORS = ["1st Floor", "2nd Floor", "3rd Floor", "Basement", "Warehouse", "Other"];

export default function Inventory() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [floor, setFloor] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);

  const loadCategories = async () => {
    const { data } = await api.get("/categories");
    setCategories(data);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (category !== "all") params.set("category", category);
      if (floor !== "all") params.set("floor", floor);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await api.get(`/inventory?${params.toString()}`);
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search, category, floor, dateFrom, dateTo]);

  const toggleSelect = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const allSelected = useMemo(() => items.length > 0 && items.every((i) => selected.includes(i.id)), [items, selected]);

  const toggleAll = () => {
    if (allSelected) setSelected([]);
    else setSelected(items.map((i) => i.id));
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category !== "all") params.set("category", category);
    if (floor !== "all") params.set("floor", floor);
    const token = localStorage.getItem("kerma_token");
    const res = await fetch(`${API}/inventory/export/csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "kerma-inventory.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFillColor(9, 9, 11);
    doc.rect(0, 0, 300, 20, "F");
    doc.setTextColor(245, 166, 35);
    doc.setFontSize(16);
    doc.text("KERMA GAMES · Inventory Report", 14, 13);
    doc.setTextColor(200);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 210, 13);

    autoTable(doc, {
      startY: 26,
      head: [["SKU", "Name", "Category", "Qty", "Location", "Floor", "Serial", "IP", "Next Maint.", "Created"]],
      body: items.map((i) => [
        i.sku, i.name, i.category, i.quantity, i.location, i.floor || "",
        i.serial_number || "", i.ip_address || "",
        i.next_maintenance ? new Date(i.next_maintenance).toLocaleDateString() : "",
        new Date(i.created_at).toLocaleDateString(),
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [24, 24, 27], textColor: [245, 166, 35] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
    doc.save("kerma-inventory.pdf");
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="inventory-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">Asset Registry</div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Inventory
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={exportCsv}
            disabled={items.length === 0}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
          >
            <FileCsv size={16} weight="duotone" /> CSV
          </button>
          <button
            onClick={exportPdf}
            disabled={items.length === 0}
            data-testid="export-pdf-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
          >
            <FilePdf size={16} weight="duotone" /> PDF
          </button>
          {selected.length > 0 && (
            <button
              onClick={() => nav(`/labels?ids=${selected.join(",")}`)}
              data-testid="print-selected-labels"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm text-[#F5A623] hover:bg-zinc-800 transition-colors"
            >
              <Barcode size={16} weight="duotone" /> Labels ({selected.length})
            </button>
          )}
          <button
            onClick={() => nav("/inventory/new")}
            data-testid="add-item-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F5A623] hover:bg-[#E67E22] text-zinc-950 font-bold text-sm transition-colors"
          >
            <Plus size={16} weight="bold" /> New Asset
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="relative md:col-span-2">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, SKU, serial…"
            data-testid="inventory-search"
            className="w-full pl-9 pr-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]"
          />
        </div>
        <select
          value={category} onChange={(e) => setCategory(e.target.value)}
          data-testid="filter-category"
          className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100 focus:outline-none focus:border-[#F5A623]"
        >
          <option value="all">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select
          value={floor} onChange={(e) => setFloor(e.target.value)}
          data-testid="filter-floor"
          className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100 focus:outline-none focus:border-[#F5A623]"
        >
          <option value="all">All Floors</option>
          {FLOORS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" className="px-2 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-xs text-zinc-300 focus:outline-none focus:border-[#F5A623]" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" className="px-2 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-xs text-zinc-300 focus:outline-none focus:border-[#F5A623]" />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg bg-zinc-900/50 border border-zinc-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead className="bg-zinc-900 border-b border-zinc-800">
              <tr className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                <th className="p-3 w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-[#F5A623]" data-testid="select-all-checkbox" />
                </th>
                <th className="p-3 text-left">Item</th>
                <th className="p-3 text-left hidden md:table-cell">SKU</th>
                <th className="p-3 text-left hidden lg:table-cell">Category</th>
                <th className="p-3 text-left hidden md:table-cell">Location</th>
                <th className="p-3 text-right">Qty</th>
                <th className="p-3 text-left hidden xl:table-cell">Next Maint.</th>
                <th className="p-3 text-left hidden lg:table-cell">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="p-8 text-center text-zinc-600 text-sm">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-12 text-center">
                    <Package size={40} className="mx-auto text-zinc-700 mb-3" />
                    <div className="text-zinc-400 text-sm mb-1">No assets match the current filters.</div>
                    <button onClick={() => nav("/inventory/new")} className="text-xs text-[#F5A623] hover:underline">+ Add your first asset</button>
                  </td>
                </tr>
              )}
              {!loading && items.map((i) => {
                const due = i.next_maintenance ? new Date(i.next_maintenance) : null;
                const overdue = due && due < new Date();
                return (
                  <tr
                    key={i.id}
                    className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors cursor-pointer"
                    data-testid={`inventory-row-${i.sku}`}
                    onClick={() => nav(`/inventory/${i.id}`)}
                  >
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.includes(i.id)} onChange={() => toggleSelect(i.id)} className="accent-[#F5A623]" />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-md bg-zinc-800 overflow-hidden grid place-items-center flex-shrink-0">
                          {i.photo
                            ? <img src={i.photo} alt={i.name} className="h-full w-full object-cover" />
                            : <Package size={14} className="text-zinc-600" />}
                        </div>
                        <div className="min-w-0">
                          <div className="text-zinc-100 truncate max-w-[220px]">{i.name}</div>
                          <div className="text-[11px] text-zinc-500 md:hidden font-mono">{i.sku}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-zinc-400 hidden md:table-cell">{i.sku}</td>
                    <td className="p-3 hidden lg:table-cell">
                      <span className="inline-block px-2 py-0.5 rounded-sm bg-zinc-800 text-xs text-zinc-300 border border-zinc-700">{i.category}</span>
                    </td>
                    <td className="p-3 text-zinc-400 hidden md:table-cell">{i.location}</td>
                    <td className="p-3 text-right font-mono text-zinc-100">{i.quantity}</td>
                    <td className="p-3 hidden xl:table-cell">
                      {due ? (
                        <span className={`font-mono text-xs px-2 py-0.5 rounded-sm ${overdue ? "bg-red-500/10 text-red-400 border border-red-900/50" : "bg-[#F5A623]/10 text-[#F5A623] border border-[#F5A623]/20"}`}>
                          {due.toLocaleDateString()}
                        </span>
                      ) : <span className="text-zinc-600 text-xs">—</span>}
                    </td>
                    <td className="p-3 font-mono text-xs text-zinc-500 hidden lg:table-cell">{new Date(i.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
